import { useState } from 'react';
import {
  Input, Badge, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Button, Table,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { CopyChip } from '../../components/CopyChip';
import { useUsageRuns, useUsageRun } from './useUsageRuns';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const money = (cents, cur = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur }).format((Number(cents) || 0) / 100);
const day = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

const STATUS_COLOR = { computed: 'info', invoiced: 'success', void: 'secondary' };
const MODE_COLOR = { overage: 'primary', payg: 'warning' };

const STATUS_FILTERS = [
  { value: '', label: 'Alle Status' },
  { value: 'computed', label: 'computed' },
  { value: 'invoiced', label: 'invoiced' },
  { value: 'void', label: 'void' },
];

/**
 * Metered-Billing · Abrechnungsläufe (usage_billing_runs).
 * Read-only Operator-Sicht. Backend: GET /v1/admin/usage-runs.
 */
export function UsageRunsPage() {
  const [status, setStatus] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [detailId, setDetailId] = useState(null);

  const query = useUsageRuns({ status: status || undefined, subscriptionId: subFilter || undefined });
  const runs = query.data?.runs ?? [];

  return (
    <div>
      <PageHeader
        title="Abrechnungsläufe (Metered)"
        subtitle={`${runs.length} Läufe · GET /v1/admin/usage-runs`}
      />

      <Alert color="info" className="small">
        Nutzungsbasierte Abrechnungsläufe (Overage/PAYG). Jeder Lauf ist idempotent
        pro Subscription+Periode; <code>invoiced</code> = eine offene Lexoffice-Rechnung
        wurde erzeugt. Read-only — Läufe entstehen durch Renewal-Kopplung bzw. den
        stündlichen Sweep.
      </Alert>

      <FilterBar
        extras={
          <div className="d-flex gap-2">
            <Input
              type="select" bsSize="sm" value={status}
              onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 160 }}
            >
              {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Input>
            <Input
              type="number" bsSize="sm" placeholder="Subscription-ID"
              value={subFilter} onChange={(e) => setSubFilter(e.target.value)}
              style={{ maxWidth: 160 }}
            />
          </div>
        }
      />

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!runs.length}
        emptyMessage="Keine Abrechnungsläufe (noch kein metered Produkt aktiv?)."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }}>ID</th>
            <th style={{ width: 120 }}>Subscription</th>
            <th style={{ width: 200 }}>Periode</th>
            <th style={{ width: 90 }}>Modus</th>
            <th style={{ width: 100 }}>Status</th>
            <th style={{ width: 120 }} className="text-end">Betrag</th>
            <th>Rechnung</th>
            <th style={{ width: 100 }} />
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id}>
              <td className="text-muted">#{r.id}</td>
              <td><CopyChip value={String(r.subscriptionId)} label="Sub" /></td>
              <td className="small">{day(r.periodStart)} – {day(r.periodEnd)}</td>
              <td><Badge color={MODE_COLOR[r.mode] || 'secondary'} pill>{r.mode}</Badge></td>
              <td><Badge color={STATUS_COLOR[r.status] || 'secondary'} pill>{r.status}</Badge></td>
              <td className="text-end fw-semibold">{money(r.totalCents, r.currency)}</td>
              <td>{r.invoiceRef ? <code className="small">{r.invoiceRef}</code> : <span className="text-muted">—</span>}</td>
              <td className="text-end">
                <Button color="primary" outline size="sm" onClick={() => setDetailId(r.id)}>Zeilen</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <RunDetailModal id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function RunDetailModal({ id, onClose }) {
  const query = useUsageRun(id);
  const run = query.data?.run;
  return (
    <Modal isOpen={id != null} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Lauf #{id} — berechnete Zeilen</ModalHeader>
      <ModalBody>
        {query.isLoading && <div className="text-muted">Lädt…</div>}
        {query.error && <Alert color="danger" className="small">{String(query.error.message || query.error)}</Alert>}
        {run && (
          <>
            <div className="small text-muted mb-2">
              {day(run.periodStart)} – {day(run.periodEnd)} · {run.mode} · {run.status}
              {run.nexusRef && <> · Nexus-Ref <code>{run.nexusRef}</code></>}
            </div>
            <Table size="sm" bordered className="small">
              <thead>
                <tr>
                  <th>Einheit</th>
                  <th className="text-end">Menge</th>
                  <th className="text-end">Inklusiv</th>
                  <th className="text-end">Berechenbar</th>
                  <th className="text-end">Rate</th>
                  <th className="text-end">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {(run.lines || []).map((l, i) => (
                  <tr key={i}>
                    <td><code>{l.unit}</code></td>
                    <td className="text-end">{l.quantity}</td>
                    <td className="text-end">{l.included ?? 0}</td>
                    <td className="text-end">{l.billable}</td>
                    <td className="text-end">{l.rateCents} ¢</td>
                    <td className="text-end fw-semibold">{money(l.amountCents, run.currency)}</td>
                  </tr>
                ))}
                {(!run.lines || run.lines.length === 0) && (
                  <tr><td colSpan={6} className="text-muted text-center">Keine berechenbaren Zeilen.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={5} className="text-end">Summe (netto)</th>
                  <th className="text-end">{fmtEur.format((Number(run.totalCents) || 0) / 100)}</th>
                </tr>
              </tfoot>
            </Table>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>Schließen</Button>
      </ModalFooter>
    </Modal>
  );
}
